import React from "react";
import { AppCard } from "../../components/ui";
import { utils } from "../../utils/utils";

export interface StructuredError {
  fileId: number;
  responseCode: number;
  fileStatus: string;
  header: {
    date: string;
    payerIban: string | null;
    currency: string | null;
    totalAmount: number;
    transactionCount: number;
  };
  recordCount: number;
  totalAmount: number;
  headerValidation: {
    isValid: boolean;
    errors: string[];
  };
  globalErrors: string[];
  recordValidations: Array<{
    lineNumber: number;
    wasValidated: boolean;
    isValid: boolean | null;
    lineContent: string | null;
    errors: string[];
  }>;
  errorLog: string | null;
}

interface ErrorDisplayProps {
  error: StructuredError | string;
  className?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, className = "" }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // If error is a string, display it as a simple alert
  if (typeof error === "string") {
    const cleanedError = utils.cleanErrorMessage(error);

    return (
      <div className={`rounded-lg p-4 bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400 mt-3 ${className}`}>
        <div className="flex items-start">
          <i className="fas fa-exclamation-triangle mr-2 mt-1"></i>
          <div className="flex-1">
            {Array.isArray(cleanedError) ? (
              cleanedError.map((err, index) => (
                <div key={index}>{err}</div>
              ))
            ) : (
              <span>{cleanedError}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const structuredError = error as StructuredError;

  // Determine the main error type and message
  const getMainErrorMessage = () => {
    if (structuredError.globalErrors.length > 0) {
      return structuredError.globalErrors[0];
    }

    if (!structuredError.headerValidation.isValid && structuredError.headerValidation.errors.length > 0) {
      return structuredError.headerValidation.errors[0];
    }

    const invalidRecords = structuredError.recordValidations.filter(r => r.isValid === false);
    if (invalidRecords.length > 0) {
      return `Found ${invalidRecords.length} invalid transaction(s) in the file`;
    }

    return "File validation failed";
  };

  const getErrorIcon = () => {
    if (structuredError.responseCode === 409) {
      return "fas fa-copy"; // Duplicate file
    }
    if (structuredError.responseCode === 400) {
      return "fas fa-exclamation-triangle"; // Validation error
    }
    return "fas fa-times-circle";
  };

  const isWarning = structuredError.responseCode === 409;

  const mainErrorMessage = getMainErrorMessage();
  const errorIcon = getErrorIcon();

  return (
    <div className={`${className}`}>
      <div className={`rounded-lg p-4 mt-3 ${
        isWarning
          ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400'
          : 'bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400'
      }`}>
        <div className="flex items-start">
          <i className={`${errorIcon} mr-2 mt-1`}></i>
          <div className="flex-1">
            <div className="font-bold mb-2">{mainErrorMessage}</div>

            {/* Show summary information */}
            <div className="mb-2">
              <small className="text-[0.8rem] text-neutral-500 dark:text-neutral-400 font-medium">
                File ID: {structuredError.fileId} |
                Status: {structuredError.fileStatus} |
                Response Code: {structuredError.responseCode}
              </small>
            </div>

            {/* Expandable details */}
            <button
              className="p-0 bg-transparent border-none cursor-pointer text-sm underline hover:no-underline text-inherit"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? (
                <>
                  <i className="fas fa-chevron-down mr-1"></i>
                  Hide Details
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-right mr-1"></i>
                  Show Details
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-[2000px]' : 'max-h-0'}`}>
        <AppCard className="mt-2">
          <AppCard.Header title="Error Details" className="py-2" />
          <AppCard.Body className="py-2">
            {/* Global Errors */}
            {structuredError.globalErrors.length > 0 && (
              <div className="mb-3">
                <h6 className="text-danger-600 dark:text-danger-400 mb-2">
                  <i className="fas fa-globe mr-1"></i>
                  Global Errors
                </h6>
                <ul className="mb-0">
                  {structuredError.globalErrors.map((error, index) => (
                    <li key={index} className="text-danger-600 dark:text-danger-400">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Header Validation Errors */}
            {!structuredError.headerValidation.isValid && structuredError.headerValidation.errors.length > 0 && (
              <div className="mb-3">
                <h6 className="text-danger-600 dark:text-danger-400 mb-2">
                  <i className="fas fa-file-alt mr-1"></i>
                  Header Validation Errors
                </h6>
                <ul className="mb-0">
                  {structuredError.headerValidation.errors.map((error, index) => (
                    <li key={index} className="text-danger-600 dark:text-danger-400">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Transaction Validation Errors */}
            {structuredError.recordValidations.some(r => r.isValid === false) && (
              <div className="mb-3">
                <h6 className="text-danger-600 dark:text-danger-400 mb-2">
                  <i className="fas fa-list mr-1"></i>
                  Transaction Errors
                </h6>
                <div>
                  {structuredError.recordValidations
                    .filter(r => r.isValid === false)
                    .map((record, index) => (
                      <div key={index} className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-md transition-all hover:from-red-100 hover:to-red-200 hover:translate-x-0.5 hover:shadow-md mb-2 p-2">
                        <div className="font-bold text-danger-600 dark:text-danger-400 text-[0.9rem]">
                          Line {record.lineNumber}
                        </div>
                        {record.lineContent && (
                          <div className="text-[0.8rem] text-neutral-500 dark:text-neutral-400 font-mono bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1 rounded border border-neutral-200 dark:border-neutral-800 small mb-1">
                            Content: {record.lineContent}
                          </div>
                        )}
                        <ul className="mb-0 small">
                          {record.errors.map((error, errorIndex) => (
                            <li key={errorIndex} className="text-danger-600 dark:text-danger-400 text-[0.85rem] font-medium">{error}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* File Information */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-md p-4 border border-cyan-200">
              <h6 className="text-neutral-700 dark:text-neutral-300 font-semibold mb-3">
                <i className="fas fa-info-circle mr-2 text-primary-500"></i>
                File Information
              </h6>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <small>
                    <strong>Date:</strong> {structuredError.header.date || 'N/A'}<br/>
                    <strong>Payer IBAN:</strong> {structuredError.header.payerIban || 'N/A'}<br/>
                    <strong>Currency:</strong> {structuredError.header.currency || 'N/A'}
                  </small>
                </div>
                <div>
                  <small>
                    <strong>Total Amount:</strong> {structuredError.header.totalAmount.toLocaleString()}<br/>
                    <strong>Transaction Count:</strong> {structuredError.header.transactionCount}<br/>
                    <strong>Record Count:</strong> {structuredError.recordCount}
                  </small>
                </div>
              </div>
            </div>
          </AppCard.Body>
        </AppCard>
      </div>
    </div>
  );
};

export default ErrorDisplay;
